import { createContext, useContext, useState, useCallback, ReactNode } from "react";

export interface GalleryItem {
  id: string;
  category: "experience" | "education" | "volunteering";
  icon?: string;
  backgroundImage?: string;
  backgroundPosition?: string;
  title: string;
  subtitle: string;
  roles?: { title: string; subtitle: string; duration?: string }[];
  startDate: string;
  endDate: string;
  overview?: { label: string; value: string }[];
  summary: string;
  contributions: string[];
  learnings: string[];
  jobOffers?: { title: string; link: string }[];
  addedAt: number;
}

/** Parse "Mon YYYY" or "Present" into a sortable timestamp (higher = more recent) */
const parseDate = (d: string): number => {
  if (!d || d.toLowerCase() === "present") return Date.now();
  const months: Record<string, number> = {
    jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
    jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
  };
  const parts = d.split(" ");
  if (parts.length === 2) {
    const month = months[parts[0].toLowerCase().slice(0, 3)] ?? 0;
    const year = parseInt(parts[1], 10);
    if (!isNaN(year)) return new Date(year, month).getTime();
  }
  // Fallback: try native parse
  const t = Date.parse(d);
  return isNaN(t) ? 0 : t;
};

/** Sort items within a category by most recent endDate first */
const sortByDate = (items: GalleryItem[]): GalleryItem[] =>
  [...items].sort((a, b) => parseDate(b.endDate) - parseDate(a.endDate));

interface GalleryContextValue {
  items: GalleryItem[];
  addItem: (item: GalleryItem) => boolean;
  removeItem: (id: string) => void;
  clearAll: () => void;
  reorderItems: (category: GalleryItem["category"], startIndex: number, endIndex: number) => void;
  hasItem: (id: string) => boolean;
  lastAdded: string | null;
  clearLastAdded: () => void;
}

const GalleryContext = createContext<GalleryContextValue | null>(null);

export const useGallery = () => {
  const ctx = useContext(GalleryContext);
  if (!ctx) throw new Error("useGallery must be inside GalleryProvider");
  return ctx;
};

export const GalleryProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [lastAdded, setLastAdded] = useState<string | null>(null);

  const addItem = useCallback((item: GalleryItem) => {
    if (items.some((i) => i.id === item.id)) return false;
    setItems((prev) => {
      const newItem = { ...item, addedAt: Date.now() };
      const catItems = [...prev.filter((i) => i.category === item.category), newItem];
      const otherItems = prev.filter((i) => i.category !== item.category);
      return [...otherItems, ...sortByDate(catItems)];
    });
    setLastAdded(item.id);
    return true;
  }, [items]);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const reorderItems = useCallback(
    (category: GalleryItem["category"], startIndex: number, endIndex: number) => {
      setItems((prev) => {
        const catItems = prev.filter((i) => i.category === category);
        const otherItems = prev.filter((i) => i.category !== category);
        const [moved] = catItems.splice(startIndex, 1);
        catItems.splice(endIndex, 0, moved);
        return [...otherItems, ...catItems];
      });
    },
    []
  );

  const hasItem = useCallback((id: string) => items.some((i) => i.id === id), [items]);

  const clearLastAdded = useCallback(() => setLastAdded(null), []);

  const clearAll = useCallback(() => {
    setItems([]);
    setLastAdded(null);
  }, []);

  return (
    <GalleryContext.Provider value={{ items, addItem, removeItem, clearAll, reorderItems, hasItem, lastAdded, clearLastAdded }}>
      {children}
    </GalleryContext.Provider>
  );
};
